import { useToastProvider } from "@deere/fuel-react";
import { ReportGmailerrorredOutlined } from "@mui/icons-material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Grid } from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import { AxiosResponse } from "axios";
import dayjs, { Dayjs } from "dayjs";
import { TFunction } from "i18next";
import { Dispatch, useContext, useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { InvoicesListContext } from "./context/InvoicesListContext";
import InvoicesGrid from "./invoicesGrid/InvoicesGrid";
import InvoicesHeader from "./invoicesHeader/InvoicesHeader";
import {
  cleanFiltersAction,
  downloadingNfeNumberAction,
  fetchInvoiceDetailsCompleteAction,
  firstPageAction,
  headerSelectChangeAction,
  lastPageAction,
  ListInvoiceActions,
  nextPageAction,
  previousPageAction,
  rowSelectChangeAction,
  setClearFilterAction,
  setD2DOrderInvoiceEnabledAction,
  setDateRangeFilterAction,
  setErrorAction,
  setFilterAction,
  setInvoicesAction,
  setIsActionsLoadingAction,
  setIsLoadingAction,
  setLanguageAction,
  setPageRowsSizeAction,
  setRowSelectionModel,
  setSelectAll,
  setSelectAllNew,
  setSortParamsAction,
  updateDealerAccountAction,
  updateSelectedAccountsAction,
} from "./reducer/action";
import {
  getInitialState,
  listInvoiceReducer,
  ListInvoiceState,
} from "./reducer/reducer";
import {
  areInvoiceFilterQueryParametersSameAsFilter,
  enableInvoiceConsolidationForCheckBox,
  enableSelectAllNew,
  enableSelectAllPages,
  enableSelectNone,
  getActionsOptions,
  getConsolidatedInvoicesExcelRequest,
  getConsolidateDownloadInProgressStatus,
  getDealerAccounts,
  getDealerAccountsList,
  getDownloadingAllInProgressStatus,
  getGridFilter,
  getInvoiceRowData,
  getlegacyInvoicesUrl,
  getRequestByFilter,
  getRequestByNumbers,
  hasAccessToClaim,
  isAuthenticationError,
  isBrazilExportDealer,
  isDebouncedSearch,
  isLoading,
  isMultipleAccountSelected,
  isTechnicalError,
  selectAllNewTooltip,
  selectAllPageTooltip,
  shouldDisableConsolidatedInvoiceDownloadButton,
  shouldDisableConsolidateInvoicesToExcelButton,
  showActions,
  showConsolidatedInvoice,
  showDownloadAll,
  showFacturaNumber,
  showHULink,
  showInvoicesGrid,
  showNFENumber,
  showOfficialDocumentNumber,
  showPaginationFooter,
  showViewedDetails,
} from "./reducer/selector";
import { InvoicesResponse } from "../../../api/invoices/interfaces";
import invoiceApi from "../../../api/invoices/invoiceApi";
import useInvoicesApi from "../../../api/invoices/useInvoiceApi";
import {
  IInvoiceExcelDownloadJobRegistrationRequest,
  IInvoiceExcelDownloadJobRegistrationRequestV2,
  IJobRegistrationResponse,
  InvoiceByAccountWithFiltersConsolidationRequest,
  InvoiceByAccountWithNumbersConsolidationRequest,
  InvoiceByFiltersConsolidationRequest,
  InvoiceByNumbersConsolidationRequest,
} from "../../../api/jobsService/IJobsService";
import JobsServiceApi from "../../../api/jobsService/jobsServiceApi";
import useJobServiceApi from "../../../api/jobsService/useJobServiceApi";
import { BusinessAction, RBACRole } from "../../../api/rbac/types";
import Pagination from "../../../components/DataGridPro/Pagination/Pagination";
import ErrorPage from "../../../components/ErrorPage/ErrorPage";
import LoadingIndicator from "../../../components/LoadingIndicator/LoadingIndicator";
import StickyFooter from "../../../components/StickyFooter/StickyFooter";
import { ConfigContext } from "../../../context/ConfigContext";
import { DealerAccountContext } from "../../../context/DealerAccountContext";
import { FeatureToggleContextV2 } from "../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { PreferenceContext } from "../../../context/PreferencesContext/PreferencesContext";
import { RBACAccessContext } from "../../../context/RBACAccessContext/RBACAccessContext";
import { UserContext } from "../../../context/UserContext/UserContext";
import { rbacCheck } from "../../../utils/rbacCheck";
import { CATEGORIES, TOGGLES } from "../../featureToggles/constants";

export const consolidateInvoicesDownloadFn = (
  JOBS_SERVICE_URL: string,
  ENABLE_INVOICES_VIEWED: boolean,
  state: ListInvoiceState,
  dispatch: Dispatch<ListInvoiceActions>,
  isJobsApiAuthEnable: boolean,
  isConsolidateMethodPreferenceEnabled: boolean,
  consolidationMethodPreference: string | undefined,
  invokeInvoiceByNumbersConsolidationDownloadWithAuth: (
    request: InvoiceByNumbersConsolidationRequest,
    accessToken: string,
    dealerLocation: string,
    lang?: string,
    viewedDetailsEnabled?: boolean,
    mruSalesOrgId?: string
  ) => Promise<AxiosResponse<IJobRegistrationResponse>>,
  invokeInvoiceByAccountWithNumbersDownloadWithAuth: (
    request: InvoiceByAccountWithNumbersConsolidationRequest,
    accessToken: string,
    dealerLocation: string,
    lang?: string,
    viewedDetailsEnabled?: boolean,
    mruSalesOrgId?: string,
    format?: "zip" | "pdf"
  ) => Promise<AxiosResponse<IJobRegistrationResponse>>,
  invokeInvoiceByAccountWithFilterDownloadWithAuth: (
    request: InvoiceByAccountWithFiltersConsolidationRequest,
    accessToken: string,
    dealerLocation: string,
    lang?: string,
    viewedDetailsEnabled?: boolean,
    mruSalesOrgId?: string,
    format?: "zip" | "pdf"
  ) => Promise<AxiosResponse<IJobRegistrationResponse>>,
  invokeInvoiceByFilterDownloadWithAuth: (
    request: InvoiceByFiltersConsolidationRequest,
    accessToken: string,
    dealerLocation: string,
    lang?: string,
    viewedDetailsEnabled?: boolean,
    mruSalesOrgId?: string
  ) => Promise<AxiosResponse<IJobRegistrationResponse>>
): Promise<AxiosResponse<IJobRegistrationResponse>> => {
  const xid = state.accessToken?.xid ?? "";
  let promise: Promise<AxiosResponse<IJobRegistrationResponse>>;
  const multipleAccountsSelected = isMultipleAccountSelected(state);
  const consolidationFormat: "zip" | "pdf" | undefined =
    isConsolidateMethodPreferenceEnabled &&
    (consolidationMethodPreference === "zip" ||
      consolidationMethodPreference === "pdf")
      ? consolidationMethodPreference
      : undefined;

  if (state.selectAll || state.selectAllNew) {
    if (multipleAccountsSelected) {
      const requestByFilter = getRequestByFilter(
        state,
        true
      ) as InvoiceByAccountWithFiltersConsolidationRequest;
      if (isJobsApiAuthEnable) {
        promise = invokeInvoiceByAccountWithFilterDownloadWithAuth(
          requestByFilter,
          state.accessToken?.accessToken ?? "",
          state.dealerLocation ?? "",
          state.language,
          ENABLE_INVOICES_VIEWED,
          state.dealerAccount?.mruSalesOrg,
          consolidationFormat
        );
      } else {
        promise = JobsServiceApi.invokeInvoiceByAccountWithFilterDownload(
          JOBS_SERVICE_URL,
          requestByFilter,
          state.accessToken?.accessToken ?? "",
          xid,
          state.dealerLocation ?? "",
          state.language,
          ENABLE_INVOICES_VIEWED,
          state.dealerAccount?.mruSalesOrg,
          consolidationFormat
        );
      }
    } else if (isJobsApiAuthEnable) {
      const requestByFilter = getRequestByFilter(
        state,
        false
      ) as InvoiceByFiltersConsolidationRequest;
      promise = invokeInvoiceByFilterDownloadWithAuth(
        requestByFilter,
        state.accessToken?.accessToken ?? "",
        state.dealerLocation ?? "",
        state.language,
        ENABLE_INVOICES_VIEWED,
        state.dealerAccount?.mruSalesOrg
      );
    } else {
      const requestByFilter = getRequestByFilter(
        state,
        false
      ) as InvoiceByFiltersConsolidationRequest;
      promise = JobsServiceApi.invokeInvoiceByFilterDownload(
        JOBS_SERVICE_URL,
        requestByFilter,
        state.accessToken?.accessToken ?? "",
        xid,
        state.dealerLocation ?? "",
        state.language,
        ENABLE_INVOICES_VIEWED,
        state.dealerAccount?.mruSalesOrg
      );
    }

    return promise.then((response) => {
      dispatch(setRowSelectionModel([]));
      dispatch(setSelectAll(false));
      dispatch(setSelectAllNew(false));
      return response;
    });
  }
  const requestByNumbers = getRequestByNumbers(state);

  if (isMultipleAccountSelected(state)) {
    if (isJobsApiAuthEnable) {
      promise = invokeInvoiceByAccountWithNumbersDownloadWithAuth(
        // @ts-ignore
        requestByNumbers,
        state.accessToken?.accessToken ?? "",
        state.dealerLocation ?? "",
        state.language,
        ENABLE_INVOICES_VIEWED,
        state.dealerAccount?.mruSalesOrg,
        isConsolidateMethodPreferenceEnabled && consolidationMethodPreference
      );
    } else {
      promise = JobsServiceApi.invokeInvoiceByAccountWithNumbersDownload(
        JOBS_SERVICE_URL,
        // @ts-ignore
        requestByNumbers,
        state.accessToken?.accessToken ?? "",
        xid,
        state.dealerLocation ?? "",
        state.language,
        ENABLE_INVOICES_VIEWED,
        state.dealerAccount?.mruSalesOrg,
        isConsolidateMethodPreferenceEnabled && consolidationMethodPreference
      );
    }
  } else if (isJobsApiAuthEnable) {
    promise = invokeInvoiceByNumbersConsolidationDownloadWithAuth(
      // @ts-ignore
      requestByNumbers,
      state.accessToken?.accessToken ?? "",
      state.dealerLocation ?? "",
      state.language,
      ENABLE_INVOICES_VIEWED,
      state.dealerAccount?.mruSalesOrg
    );
  } else {
    promise = JobsServiceApi.invokeInvoiceByNumbersConsolidationDownload(
      JOBS_SERVICE_URL,
      // @ts-ignore
      requestByNumbers,
      state.accessToken?.accessToken ?? "",
      xid,
      state.dealerLocation ?? "",
      state.language,
      ENABLE_INVOICES_VIEWED,
      state.dealerAccount?.mruSalesOrg
    );
  }

  return promise.then((response) => {
    dispatch(setRowSelectionModel([]));
    return response;
  });
};

export const downloadFunction: (
  state: ListInvoiceState,
  JOBS_SERVICE_URL: string,
  ENABLE_HU_FOR_INVOICES: boolean,
  ENABLE_INVOICE_LEADING_ZEROS: boolean,
  isJobsApiAuthEnable: boolean,
  invokeInvoiceExcelDownload: (
    request: IInvoiceExcelDownloadJobRegistrationRequest,
    lang?: string
  ) => Promise<AxiosResponse<IJobRegistrationResponse>>
) => Promise<AxiosResponse<IJobRegistrationResponse>> = (
  state: ListInvoiceState,
  JOBS_SERVICE_URL: string,
  ENABLE_HU_FOR_INVOICES: boolean,
  ENABLE_INVOICE_LEADING_ZEROS: boolean,
  isJobsApiAuthEnable: boolean,
  invokeInvoiceExcelDownloadWithAuth: (
    request: IInvoiceExcelDownloadJobRegistrationRequest,
    lang?: string
  ) => Promise<AxiosResponse<IJobRegistrationResponse>>
) => {
  const requestBody: IInvoiceExcelDownloadJobRegistrationRequest = {
    accountId: getDealerAccounts(state),
    withHandlingUnits: ENABLE_HU_FOR_INVOICES,
    withOfficialDocumentNumbers: isBrazilExportDealer(state),
    withLeadingZeros: ENABLE_INVOICE_LEADING_ZEROS,
    dealerLocation: state.dealerLocation,
  };

  if (isJobsApiAuthEnable) {
    return invokeInvoiceExcelDownloadWithAuth(
      requestBody,
      state.language
    ).catch((apiError: any) => Promise.reject(apiError));
  }
  return JobsServiceApi.invokeInvoiceExcelDownload(
    JOBS_SERVICE_URL,
    requestBody,
    state.accessToken?.accessToken ?? "",
    state.language
  ).catch((apiError: any) => Promise.reject(apiError));
};

export const downloadFunctionV2: (
  state: ListInvoiceState,
  JOBS_SERVICE_URL: string,
  ENABLE_HU_FOR_INVOICES: boolean,
  ENABLE_INVOICE_LEADING_ZEROS: boolean,
  isJobsApiAuthEnable: boolean,
  invokeInvoiceExcelDownloadV2: (
    request: IInvoiceExcelDownloadJobRegistrationRequestV2,
    lang?: string
  ) => Promise<AxiosResponse<IJobRegistrationResponse>>,
  enableFilterSort: boolean
) => Promise<AxiosResponse<IJobRegistrationResponse>> = (
  state: ListInvoiceState,
  JOBS_SERVICE_URL: string,
  ENABLE_HU_FOR_INVOICES: boolean,
  ENABLE_INVOICE_LEADING_ZEROS: boolean,
  isJobsApiAuthEnable: boolean,
  invokeInvoiceExcelDownloadV2: (
    request: IInvoiceExcelDownloadJobRegistrationRequestV2,
    lang?: string
  ) => Promise<AxiosResponse<IJobRegistrationResponse>>,
  enableFilterSort: boolean
) => {
  const requestBody: IInvoiceExcelDownloadJobRegistrationRequestV2 = {
    accountIds: getDealerAccountsList(state),
    withHandlingUnits: ENABLE_HU_FOR_INVOICES,
    withOfficialDocumentNumbers: isBrazilExportDealer(state),
    withLeadingZeros: ENABLE_INVOICE_LEADING_ZEROS,
    dealerLocation: state.dealerLocation,
    ...(enableFilterSort && {
      invoiceNumber: state.filter.filters.invoiceNumber,
      invoiceType: state.filter.filters.invoiceType,
      ...(state.filter.filters.srcProcessType && {
        srcProcessType: state.filter.filters.srcProcessType,
      }),
      nfeNumber: state.filter.filters.nfeNumber,
      documentNumber: state.filter.filters.documentNumber,
      huNumber: state.filter.filters.handlingUnitNumber,
      dealerReference: state.filter.filters.dealerReference,
      officialDocumentNumber: state.filter.filters.officialDocumentNumber,
      partNumber: state.filter.filters.partNumber,
      startDate: state.filter.filters.startDate.format("YYYY-MM-DD"),
      endDate: state.filter.filters.endDate.format("YYYY-MM-DD"),
      dealerId: state.filter.filters.dealerId,
      factura: state.filter.filters.factura,
      sortField: state.filter.sortField ?? undefined,
      sortDir: state.filter.sortDir ?? undefined,
    }),
  };

  if (isJobsApiAuthEnable) {
    return invokeInvoiceExcelDownloadV2(requestBody, state.language).catch(
      (apiError: any) => Promise.reject(apiError)
    );
  }
  return JobsServiceApi.invokeInvoiceExcelDownloadV2(
    JOBS_SERVICE_URL,
    requestBody,
    state.accessToken?.accessToken ?? "",
    state.language
  ).catch((apiError: any) => Promise.reject(apiError));
};

const fetchInvoices = async ({
  INVOICE_SERVICE_URL,
  state,
  dispatch,
  isInvoicesApiAuthEnable,
  getInvoices,
}: {
  INVOICE_SERVICE_URL: string;
  state: ListInvoiceState;
  dispatch: Dispatch<ListInvoiceActions>;
  isInvoicesApiAuthEnable: boolean;
  getInvoices: (
    accountId: string,
    lang?: string,
    pageSize?: number,
    pageNumber?: number,
    startDate?: string,
    endDate?: string,
    orderBy?: string,
    sortBy?: string,
    invoiceNumber?: string,
    documentNumber?: string,
    dealerReference?: string,
    invoiceType?: string,
    partNumber?: string,
    huNumber?: string,
    nfeNumber?: string,
    officialDocumentNumber?: string,
    withHandlingUnits?: boolean,
    withLeadingZeros?: boolean,
    dealerId?: string,
    factura?: string,
    mru?: string,
    country?: string,
    srcProcessType?: string
  ) => Promise<InvoicesResponse>;
}) => {
  dispatch(setIsLoadingAction(true));
  const filter = state.filter.filters;
  try {
    const invoicesResponse = isInvoicesApiAuthEnable
      ? await getInvoices(
          getDealerAccounts(state),
          state.language,
          state.pageSize,
          state.page,
          filter.startDate.toISOString().split("T")[0],
          filter.endDate.toISOString().split("T")[0],
          state.filter.sortDir?.toUpperCase(),
          state.filter.sortField,
          filter?.invoiceNumber ?? "",
          filter?.documentNumber ?? "",
          filter?.dealerReference ?? "",
          filter.invoiceType,
          filter?.partNumber ?? "",
          filter?.handlingUnitNumber ?? "",
          filter?.nfeNumber ?? "",
          filter?.officialDocumentNumber ?? "",
          state.featureToggles.enableHUForInvoices,
          state.featureToggles.enableInvoiceLeadingZeros,
          filter?.dealerId ?? "",
          filter?.factura ?? "",
          state.dealerAccount?.mruSalesOrg ?? "",
          state.dealerLocation,
          filter.srcProcessType ?? ""
        )
      : await invoiceApi.getInvoices(
          INVOICE_SERVICE_URL,
          getDealerAccounts(state),
          state?.accessToken?.accessToken ?? "",
          state.language,
          state.pageSize,
          state.page,
          filter.startDate.toISOString().split("T")[0],
          filter.endDate.toISOString().split("T")[0],
          state.filter.sortDir?.toUpperCase(),
          state.filter.sortField,
          filter?.invoiceNumber ?? "",
          filter?.documentNumber ?? "",
          filter?.dealerReference ?? "",
          filter.invoiceType,
          filter?.partNumber ?? "",
          filter?.handlingUnitNumber ?? "",
          filter?.nfeNumber ?? "",
          filter?.officialDocumentNumber ?? "",
          state.featureToggles.enableHUForInvoices,
          state.featureToggles.enableInvoiceLeadingZeros,
          filter?.dealerId ?? "",
          filter?.factura ?? "",
          state.dealerAccount?.mruSalesOrg ?? "",
          state.dealerLocation,
          filter.srcProcessType ?? ""
        );
    dispatch(setInvoicesAction(invoicesResponse));
  } catch (e) {
    dispatch(setErrorAction(e));
  } finally {
    dispatch(setIsLoadingAction(false));
  }
};

const Invoices = () => {
  const { authState } = useOktaAuth();
  const {
    INVOICE_SERVICE_URL,
    JOBS_SERVICE_URL,
    JDPOINT_INVCS_URL,
    ENABLE_HU_FOR_INVOICES,
    ENABLE_INVOICE_LEADING_ZEROS,
    ENABLE_CLAIMS_CHINA,
    ENABLE_CLAIMS_INDIA,
    ENABLE_INVOICES_VIEWED,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_JOBS_SERVICE,
    ENABLE_INVOICE_EXCEL_DOWNLOAD_V2,
    ENABLE_CONSOLIDATION_METHOD_PREFERENCE,
    ENABLE_MULTIPLE_ACCOUNTS,
    ENABLE_DEALER_ID_COLUMN_PERSISTENCE_IN_HU,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
    ENABLE_INVOICE_PERSIST_FILTER_ON_SESSION,
    ENABLE_D2D,
    ENABLE_CONSOLIDATE_INVOICES_TO_EXCEL,
  } = useContext(ConfigContext);

  const isInvoicesApiAuthEnable = ENABLE_RBAC_AUTH_SUPPORT;

  const isJobsApiAuthEnable =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_JOBS_SERVICE;

  const {
    getInvoices,
    getInvoicePDF,
    getInvoiceDetailsV2,
    getConsolidatedInvoicesLineItemsAsExcel,
  } = useInvoicesApi();
  const {
    invokeInvoiceExcelDownload,
    invokeInvoiceByNumbersConsolidationDownload,
    invokeInvoiceByAccountWithNumbersDownload,
    invokeInvoiceByAccountWithFilterDownload,
    invokeInvoiceByFilterDownload,
    invokeInvoiceExcelDownloadV2,
  } = useJobServiceApi();

  const { isRBACEnabled, isCorporateViewEnabled } =
    useContext(RBACAccessContext);
  const { selectedAccounts, dealerAccount, dealerLocation } =
    useContext(DealerAccountContext);
  const user = useContext(UserContext);
  // @ts-ignore — react-i18next type depth exceeds TS limit with large translation namespaces
  const { t, i18n } = useTranslation(["common", "invoices"]);
  const { isToggleEnabled, isToggleEnabledWithAccountId } = useContext(
    FeatureToggleContextV2
  );
  const navigate = useNavigate();
  const { enqueueToast } = useToastProvider();
  const { userPreferences } = useContext(PreferenceContext);
  const consolidationMethodPreference = userPreferences?.find(
    (preference) => preference.key === "consolidation_method"
  )?.value;
  const isJDPSDealer = user?.is(RBACRole.JDPS_SERVICE_DEALER);

  const isInvoiceExcelDownloadV2Enabled = ENABLE_INVOICE_EXCEL_DOWNLOAD_V2;

  const showRedirectTOJDPointLegacy =
    isToggleEnabled(CATEGORIES.INVOICE_SERVICE, TOGGLES.ENABLE_LINK_JDPOINT) &&
    !isJDPSDealer;

  const enableInvoiceConsolidation =
    isToggleEnabled(
      CATEGORIES.INVOICE_SERVICE,
      TOGGLES.ENABLE_INVOICE_CONSOLIDATION
    ) && !isJDPSDealer;

  const enableD2DOrderInvoice =
    ENABLE_D2D && isToggleEnabled(CATEGORIES.D2D_SERVICE, TOGGLES.ENABLE_D2D);

  const location = useLocation();
  const deepLinkParams = new URLSearchParams(location.hash.replace(/^#/, ""));
  const deepLinkDealerReference = deepLinkParams.get("dealerReference");
  const deepLinkInvoiceType = deepLinkParams.get("invoiceType");
  const deepLinkSourceProcessType = deepLinkParams.get("srcProcessType");

  const { getInvoicesListContext, setInvoicesListContext } =
    useContext(InvoicesListContext);

  const [state, dispatch] = useReducer(listInvoiceReducer, undefined, () => {
    const initialState = getInitialState(
      dayjs(),
      i18n.language,
      {
        ENABLE_HU_FOR_INVOICES,
        ENABLE_INVOICE_LEADING_ZEROS,
        enableCreateReturns: isToggleEnabled(
          CATEGORIES.RETURNS,
          TOGGLES.ENABLE_CREATE_RETURNS
        ),
        enableClaims: isToggleEnabled(
          CATEGORIES.CLAIMS_SERVICE,
          TOGGLES.ENABLE_CLAIMS
        ),
        enableClaimsByHU: isToggleEnabled(
          CATEGORIES.CLAIMS_SERVICE,
          TOGGLES.ENABLE_CLAIMS_BY_HU
        ),
        ENABLE_CLAIMS_CHINA,
        ENABLE_CLAIMS_INDIA,
        showRedirectTOJDPointLegacy,
        enableVendorDirectReturn: isToggleEnabled(
          CATEGORIES.RETURNS,
          TOGGLES.ENABLE_VENDOR_DIRECT_RETURN
        ),
        enableInvoiceConsolidation,
        enableMultipleAccounts: ENABLE_MULTIPLE_ACCOUNTS,
        enableCorporateViewDpaRole: ENABLE_CORPORATE_VIEW_DPA_ROLE,
        enableD2DOrderInvoice,
      },
      isRBACEnabled,
      isCorporateViewEnabled,
      {
        returns: {
          createEnabled: rbacCheck(
            isRBACEnabled,
            user,
            BusinessAction.CREATE_RETURN
          ),
        },
        handlingUnits: {
          viewEnabled: rbacCheck(
            isRBACEnabled,
            user,
            BusinessAction.VIEW_HANDLING_UNITS
          ),
        },
        claims: {
          viewEnabled: rbacCheck(
            isRBACEnabled,
            user,
            BusinessAction.VIEW_CLAIMS
          ),
          createEnabled: rbacCheck(
            isRBACEnabled,
            user,
            BusinessAction.CREATE_CLAIM
          ),
        },
      },
      selectedAccounts,
      dealerAccount,
      dealerLocation,
      {
        isAuthenticated: authState?.isAuthenticated,
        accessToken: authState?.accessToken?.accessToken,
        xid: authState?.accessToken?.claims.userID as string,
      },
      user
    );

    if (ENABLE_INVOICE_PERSIST_FILTER_ON_SESSION) {
      const savedState = getInvoicesListContext();
      if (savedState) {
        initialState.filter = {
          ...initialState.filter,
          filters: savedState.filters,
          sortField: savedState.sortField ?? initialState.filter.sortField,
          sortDir: savedState.sortDir ?? initialState.filter.sortDir,
          pageNumber: savedState.pageNumber,
          pageSize: savedState.pageSize,
        };
        initialState.page = savedState.pageNumber;
        initialState.pageSize = savedState.pageSize;
      }
    }

    if (deepLinkDealerReference) {
      initialState.filter.filters.dealerReference = deepLinkDealerReference;
    }
    if (deepLinkInvoiceType) {
      initialState.filter.filters.invoiceType = deepLinkInvoiceType;
    }
    if (deepLinkSourceProcessType) {
      initialState.filter.filters.srcProcessType = deepLinkSourceProcessType;
    }

    return initialState;
  });

  const applyFilter = async (listInvoiceState: ListInvoiceState) => {
    dispatch(firstPageAction());
    dispatch(cleanFiltersAction());
    let updatedState = listInvoiceReducer(listInvoiceState, firstPageAction());
    updatedState = listInvoiceReducer(updatedState, cleanFiltersAction());
    await fetchInvoices({
      INVOICE_SERVICE_URL,
      state: updatedState,
      dispatch,
      isInvoicesApiAuthEnable,
      getInvoices,
    });
  };

  const applyFilterDebounced = useDebouncedCallback(applyFilter, 2000);

  // Sync selectedAccounts and dealerAccount from context
  useEffect(() => {
    const accountsChanged =
      JSON.stringify(state.selectedAccounts) !==
        JSON.stringify(selectedAccounts) ||
      (dealerAccount &&
        JSON.stringify(state.dealerAccount) !== JSON.stringify(dealerAccount));

    if (accountsChanged) {
      if (
        JSON.stringify(state.selectedAccounts) !==
        JSON.stringify(selectedAccounts)
      ) {
        dispatch(updateSelectedAccountsAction(selectedAccounts));
      }
      if (
        dealerAccount &&
        JSON.stringify(state.dealerAccount) !== JSON.stringify(dealerAccount)
      ) {
        dispatch(updateDealerAccountAction(dealerAccount));
      }
    }
  }, [selectedAccounts, dealerAccount]);

  // Sync language
  useEffect(() => {
    if (state.language !== i18n.language) {
      dispatch(setLanguageAction(i18n.language));
    }
  }, [i18n.language, state.language]);

  useEffect(() => {
    let isCurrentAccount = true;

    const syncD2DOrderInvoice = async () => {
      const country = dealerAccount?.location?.country ?? dealerLocation;
      const accountId = dealerAccount?.id;
      const isD2DOrderInvoiceEnabled =
        ENABLE_D2D && country && accountId
          ? await isToggleEnabledWithAccountId(
              CATEGORIES.D2D_SERVICE,
              TOGGLES.ENABLE_D2D,
              country,
              accountId
            )
          : false;

      if (isCurrentAccount) {
        dispatch(setD2DOrderInvoiceEnabledAction(isD2DOrderInvoiceEnabled));
      }
    };

    syncD2DOrderInvoice();

    return () => {
      isCurrentAccount = false;
    };
  }, [
    ENABLE_D2D,
    dealerAccount?.id,
    dealerAccount?.location?.country,
    dealerLocation,
    isToggleEnabledWithAccountId,
  ]);

  // Fetch invoices when selectedAccounts or dealerAccount changes in state
  useEffect(() => {
    // Only fetch if the state has been synced with context
    if (
      JSON.stringify(state.selectedAccounts) ===
        JSON.stringify(selectedAccounts) &&
      (!dealerAccount ||
        JSON.stringify(state.dealerAccount) === JSON.stringify(dealerAccount))
    ) {
      const fetchData = async () => {
        await fetchInvoices({
          INVOICE_SERVICE_URL,
          state,
          dispatch,
          isInvoicesApiAuthEnable,
          getInvoices,
        });
      };
      fetchData();
    }
  }, [
    state.selectedAccounts,
    state.dealerAccount,
    selectedAccounts,
    dealerAccount,
  ]);

  useEffect(
    () => () => {
      if (ENABLE_INVOICE_PERSIST_FILTER_ON_SESSION) {
        setInvoicesListContext({
          filters: state.filter.filters,
          sortField: state.filter.sortField,
          sortDir: state.filter.sortDir,
          pageNumber: state.page,
          pageSize: state.pageSize,
        });
      }
    },
    [state.filter, state.page, state.pageSize]
  );

  const actionOptions = getActionsOptions(state);

  return (
    <Grid
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        minHeight: "72vh",
      }}
    >
      {showInvoicesGrid(state) && (
        <Box sx={{ width: "100%" }}>
          <InvoicesHeader
            legacyInventoryUrl={getlegacyInvoicesUrl(state, JDPOINT_INVCS_URL)}
            totalInvoices={state.invoicesResponse.total}
            showJDPointLegacyButton={
              state.featureToggles.showRedirectTOJDPointLegacy
            }
          />
          <InvoicesGrid
            data={getInvoiceRowData(state)}
            invoicesGridFilterProps={{
              filters: state.filter.filters,
              disableFilters: false,
              setFilterValue: (value, type) => {
                dispatch(setFilterAction(value, type));
                const updatedState = listInvoiceReducer(
                  state,
                  setFilterAction(value, type)
                );
                if (isDebouncedSearch(type)) {
                  applyFilterDebounced(updatedState);
                } else {
                  applyFilter(updatedState);
                }
              },
              clearFilterValue: (type) => {
                dispatch(setClearFilterAction(type));
                const updatedState = listInvoiceReducer(
                  state,
                  setClearFilterAction(type)
                );
                applyFilter(updatedState);
              },
              resetFilters: () => {},
              filterDateMatchDefaultDates: false,
              areInvoiceFilterQueryParametersSameAsFilter:
                areInvoiceFilterQueryParametersSameAsFilter(state.filter),
              onDateRangeChange: (startDate: Dayjs, endDate: Dayjs) => {
                if (startDate.isValid() && endDate.isValid()) {
                  const dateRangeFilterAction = setDateRangeFilterAction(
                    startDate,
                    endDate
                  );
                  dispatch(dateRangeFilterAction);
                  const updatedState = listInvoiceReducer(
                    state,
                    dateRangeFilterAction
                  );
                  applyFilter(updatedState);
                }
              },
              onSortModelChange: (model) => {
                const sortParamsAction = setSortParamsAction(
                  model[0]?.field,
                  model[0]?.sort
                );
                dispatch(sortParamsAction);
                const updatedState = listInvoiceReducer(
                  state,
                  sortParamsAction
                );
                if (
                  model[0] !== undefined &&
                  (state.filter.sortField !== model[0].field ||
                    state.filter.sortDir !== model[0].sort)
                ) {
                  applyFilter(updatedState);
                }
              },
            }}
            invoicesColumnVisibilityProps={{
              showNFENumber: showNFENumber(state),
              showFacturaNumber: showFacturaNumber(state),
              showOfficialDocumentNumber: showOfficialDocumentNumber(state),
              showViewedDate: showViewedDetails(state),
              showViewedBy: showViewedDetails(state),
              showActions: showActions(state),
              showDealerAccount: isMultipleAccountSelected(state),
              enableInvoiceConsolidationForCheckBox:
                enableInvoiceConsolidationForCheckBox(state) ||
                ENABLE_CONSOLIDATE_INVOICES_TO_EXCEL,
            }}
            dealerAccountId={getDealerAccounts(state)}
            invoicesGridContentVisibilityProps={{
              showHULink: showHULink(state),
              hasAccessToViewClaim: hasAccessToClaim(
                {
                  isViewAction: true,
                  isCreateAction: false,
                },
                state,
                (listInvoiceState) =>
                  listInvoiceState.invoicesResponse.invoices.some(
                    (invoice) => invoice.isVendorDirect
                  )
              ),
              downloadingNFeNumber: state.downloadingNFeNumber,
            }}
            checkBoxSelectionProp={{
              handleCheckboxSelection: (newRowSelectionModel) => {
                dispatch(setRowSelectionModel(newRowSelectionModel));
              },
              rowSelectionModel: state.rowSelectionModel,
            }}
            selectDropDownMenuOptions={{
              enableSelectAllPages: enableSelectAllPages(state),
              enableSelectAllNew: enableSelectAllNew(state),
              enableSelectNone: enableSelectNone(state),
              selectAllPageTooltip: getSelectDropDownToolTips(
                t,
                selectAllPageTooltip(state)
              ),
              selectAllNewTooltip: getSelectDropDownToolTips(
                t,
                selectAllNewTooltip(state)
              ),
              onSelectAllPages: () => {
                dispatch(setSelectAll(true));
              },
              onSelectAllNew: () => {
                dispatch(setSelectAllNew(true));
              },
              onSelectNone: () => {
                dispatch(setSelectAll(false));
                dispatch(setSelectAllNew(false));
              },
            }}
            invoicesToolbarProps={{
              showDownloadButton: isInvoiceExcelDownloadV2Enabled
                ? true
                : showDownloadAll(state),
              showConsolidateInvoiceButton: showConsolidatedInvoice(state),
              disableConsolidatedInvoiceDownloadButton:
                shouldDisableConsolidatedInvoiceDownloadButton(state),
              isConsolidateDownloadInProgress:
                getConsolidateDownloadInProgressStatus(state),
              isDownloadingAllInProgress:
                getDownloadingAllInProgressStatus(state),
              downloadInvoicesCallback: async () =>
                isInvoiceExcelDownloadV2Enabled
                  ? Promise.resolve(
                      downloadFunctionV2(
                        state,
                        JOBS_SERVICE_URL,
                        ENABLE_HU_FOR_INVOICES,
                        ENABLE_INVOICE_LEADING_ZEROS,
                        isJobsApiAuthEnable,
                        invokeInvoiceExcelDownloadV2,
                        ENABLE_INVOICE_PERSIST_FILTER_ON_SESSION
                      )
                    )
                  : Promise.resolve(
                      downloadFunction(
                        state,
                        JOBS_SERVICE_URL,
                        ENABLE_HU_FOR_INVOICES,
                        ENABLE_INVOICE_LEADING_ZEROS,
                        isJobsApiAuthEnable,
                        invokeInvoiceExcelDownload
                      )
                    ),
              consolidateInvoicesCallback: async () =>
                Promise.resolve(
                  consolidateInvoicesDownloadFn(
                    JOBS_SERVICE_URL,
                    ENABLE_INVOICES_VIEWED,
                    state,
                    dispatch,
                    isJobsApiAuthEnable,
                    ENABLE_CONSOLIDATION_METHOD_PREFERENCE,
                    consolidationMethodPreference,
                    invokeInvoiceByNumbersConsolidationDownload,
                    invokeInvoiceByAccountWithNumbersDownload,
                    invokeInvoiceByAccountWithFilterDownload,
                    invokeInvoiceByFilterDownload
                  )
                ),
              showConsolidateInvoicesToExcelButton:
                ENABLE_CONSOLIDATE_INVOICES_TO_EXCEL,
              disableConsolidateInvoicesToExcelButton:
                shouldDisableConsolidateInvoicesToExcelButton(state),
              consolidateInvoicesToExcelCallback: async (accessToken) => {
                const request = getConsolidatedInvoicesExcelRequest(state);
                return isInvoicesApiAuthEnable
                  ? getConsolidatedInvoicesLineItemsAsExcel(request)
                  : invoiceApi.getConsolidatedInvoicesLineItemsAsExcel(
                      INVOICE_SERVICE_URL,
                      request,
                      accessToken
                    );
              },
              onConsolidateInvoicesToExcelSuccess: () => {
                dispatch(setRowSelectionModel([]));
              },
            }}
            toggleProps={{
              showViewedDetail: showViewedDetails(state),
            }}
            isInvoicesLoading={state.isLoading}
            error={false}
            invoicesFilterState={getGridFilter(state)}
            callbacks={{
              reloadCallback: () => {},
              handleNFEClick: async (invoiceNumber, nfeNumber) => {
                try {
                  dispatch(downloadingNfeNumberAction(nfeNumber));
                  const nfeDownloadResponse = isInvoicesApiAuthEnable
                    ? await getInvoicePDF(
                        invoiceNumber || "",
                        dealerAccount?.id as string,
                        "ZN55",
                        dealerLocation,
                        "pt_BR",
                        nfeNumber as string
                      )
                    : await invoiceApi.getInvoicePDF(
                        INVOICE_SERVICE_URL,
                        invoiceNumber || "",
                        dealerAccount?.id as string,
                        state.accessToken?.accessToken || "",
                        "ZN55",
                        dealerLocation,
                        "pt_BR",
                        nfeNumber as string
                      );
                  if (nfeDownloadResponse?.data.size === 0) {
                    enqueueToast({
                      variant: "warning",
                      message: t(
                        "invoices:notifications.nfePDFFileDoesNotExistToastMessage",
                        { nfeID: nfeNumber }
                      ),
                      isCloseable: true,
                    });
                  } else {
                    const file = new Blob([nfeDownloadResponse.data], {
                      type: "application/pdf",
                    });
                    const fileURL = URL.createObjectURL(file);
                    global.open(fileURL);
                  }
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                } catch (e) {
                  enqueueToast({
                    variant: "error",
                    message: t(
                      "invoices:notifications.nfePDFFileDownloadFailureToastMessage"
                    ),
                    isCloseable: true,
                  });
                } finally {
                  dispatch(downloadingNfeNumberAction(""));
                }
              },
              setUnauthorizedError: () => {},
              selectionChanges: {
                onHeaderSelectChange: (value) => {
                  dispatch(headerSelectChangeAction(value));
                },
                onRowSelectChange: (value, rowId) => {
                  dispatch(rowSelectChangeAction(value, rowId));
                },
              },
            }}
            invoicesGridActionsMenu={{
              onActionOpen: async (
                invoiceNumber,
                dealerAccountId,
                mruSalesOrg
              ) => {
                dispatch(setIsActionsLoadingAction(true));

                const accountId =
                  dealerAccountId ?? state.dealerAccount?.id ?? "";
                const salesOrg =
                  mruSalesOrg ?? state.dealerAccount?.mruSalesOrg ?? "";

                const invoiceDetails = isInvoicesApiAuthEnable
                  ? await getInvoiceDetailsV2(
                      invoiceNumber || "",
                      accountId,
                      salesOrg,
                      state.dealerLocation,
                      state.language
                    )
                  : await invoiceApi.getInvoiceDetailsV2(
                      INVOICE_SERVICE_URL,
                      invoiceNumber || "",
                      state.accessToken?.accessToken ?? "",
                      accountId,
                      salesOrg,
                      state.dealerLocation,
                      state.language
                    );

                const invoiceDetailsWithAccount = {
                  ...invoiceDetails,
                  dealerAccount: accountId,
                };

                dispatch(
                  fetchInvoiceDetailsCompleteAction(invoiceDetailsWithAccount)
                );
                dispatch(setIsActionsLoadingAction(false));
              },
              onActionClose: () => {},
              actions: (() => {
                const dealerAccountParam =
                  state.actions.invoicesDetails?.dealerAccount ??
                  state.dealerAccount?.id ??
                  "";
                const invoiceNumber =
                  state.actions.invoicesDetails?.invoiceNumber ?? "";

                return {
                  returns: {
                    ...actionOptions.actions.returns,
                    onCreateReturnClick: (accountId: string) => {
                      navigate(
                        `/returns/create?invoiceNumber=${state.actions.invoicesDetails?.invoiceNumber}&nfeNumber=${state.actions.invoicesDetails?.nfeNumber}&dealerId=${accountId}`
                      );
                    },
                  },
                  claims: {
                    ...actionOptions.actions.claims,
                    onCreateClaimsClick: () => {
                      navigate(
                        `/claim/create?invoiceNumber=${invoiceNumber}&dealerAccountId=${dealerAccountParam}`
                      );
                    },
                  },
                  vendorDirectClaims: {
                    ...actionOptions.actions.vendorDirect,
                    onCreateVendorDirectClaimClick: () => {
                      navigate(
                        `/claim/create?invoiceNumber=${invoiceNumber}&dealerAccountId=${dealerAccountParam}`
                      );
                    },
                  },
                };
              })(),
              isLoading: actionOptions.isLoading,
            }}
            isDealerIdPersistenceEnabledForHU={
              ENABLE_DEALER_ID_COLUMN_PERSISTENCE_IN_HU
            }
          />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexGrow: 1,
        }}
      >
        {isAuthenticationError(state) && (
          <ErrorPage
            title={t("common:authentication.failed.title")}
            subtitles={[t("common:authentication.failed.subtitle")]}
            icon={{ Component: WarningAmberIcon, color: "warning" }}
            showLoginBtn
            showBackToHomeLink={false}
            fullPage
          />
        )}
        {isTechnicalError(state) && (
          <ErrorPage
            title={t("common:technicalError.title")}
            subtitles={[t("common:technicalError.subtitle")]}
            icon={{ Component: ReportGmailerrorredOutlined, color: "error" }}
            showBackToHomeLink={false}
            fullPage
          />
        )}
        {isLoading(state) && <LoadingIndicator />}{" "}
      </Box>
      {showPaginationFooter(state) && (
        <StickyFooter>
          <Pagination
            rowsPerPage={state.pageSize}
            currentPage={state.page + 1}
            totalCount={state.invoicesResponse.total}
            onRowsPerPageChange={async (pageRowSize) => {
              dispatch(setPageRowsSizeAction(pageRowSize));
              const updatedState = listInvoiceReducer(
                state,
                setPageRowsSizeAction(pageRowSize)
              );
              await fetchInvoices({
                INVOICE_SERVICE_URL,
                state: updatedState,
                dispatch,
                isInvoicesApiAuthEnable,
                getInvoices,
              });
            }}
            onFirstPageClick={async () => {
              dispatch(firstPageAction());
              const updatedState = listInvoiceReducer(state, firstPageAction());
              await fetchInvoices({
                INVOICE_SERVICE_URL,
                state: updatedState,
                dispatch,
                isInvoicesApiAuthEnable,
                getInvoices,
              });
            }}
            onLastPageClick={async () => {
              dispatch(lastPageAction());
              const updatedState = listInvoiceReducer(state, lastPageAction());
              await fetchInvoices({
                INVOICE_SERVICE_URL,
                state: updatedState,
                dispatch,
                isInvoicesApiAuthEnable,
                getInvoices,
              });
            }}
            onPreviousClick={async () => {
              dispatch(previousPageAction());
              const updatedState = listInvoiceReducer(
                state,
                previousPageAction()
              );
              await fetchInvoices({
                INVOICE_SERVICE_URL,
                state: updatedState,
                dispatch,
                isInvoicesApiAuthEnable,
                getInvoices,
              });
            }}
            onNextClick={async () => {
              dispatch(nextPageAction());
              const updatedState = listInvoiceReducer(state, nextPageAction());
              await fetchInvoices({
                INVOICE_SERVICE_URL,
                state: updatedState,
                dispatch,
                isInvoicesApiAuthEnable,
                getInvoices,
              });
            }}
          />
        </StickyFooter>
      )}
    </Grid>
  );
};

export default Invoices;

const getSelectDropDownToolTips = (
  t: TFunction<"translation", undefined>,
  text: string
) => {
  const tooltips: { [key: string]: string } = {
    DATE_RANGE_GREATER_THAN_31_DAYS: t(
      "invoices:allInvoices.dropDown.filterDateRange"
    ),
    NO_NEW_INVOICES: t("invoices:allInvoices.dropDown.noNewInvoices"),
  };
  return tooltips[text];
};
